import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, Building2, CheckCircle2, Clock, Factory, FileWarning, Loader2, Recycle, Truck } from "lucide-react";
import { errMsg, useApi } from "@/lib/api";

export const ROLE_LABEL = { generador: "Empresa Generadora", gestor: "Empresa Gestora / Destinataria", transportista: "Empresa de Transporte" };

export const CompanyStatus = ({ status }) => (
  status === "validada"
    ? <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#00f2a1]/15 text-[#007a50]" data-testid="company-status-validated"><CheckCircle2 className="w-3.5 h-3.5" />Validada</span>
    : status === "incompleta"
      ? <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600" data-testid="company-status-incomplete"><FileWarning className="w-3.5 h-3.5" />Faltan documentos</span>
      : <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700" data-testid="company-status-pending"><Clock className="w-3.5 h-3.5" />Pendiente de validación</span>
);

const GriSection = ({ list }) => {
  const gens = (list || []).filter((c) => c.role === "generador");
  const ready = gens.filter((c) => c.status === "validada");
  return (
    <section id="gri-306" className="bg-[#0a1f18] text-white rounded-3xl p-6 space-y-3 scroll-mt-24" data-testid="companies-gri-section">
      <p className="flex items-center gap-2 font-semibold"><BarChart3 className="w-5 h-5 text-[#00f2a1]" />Informe GRI 306 · Residuos</p>
      {ready.length ? (
        <div className="flex flex-wrap gap-2">
          {ready.map((c) => (
            <Link key={c.id} to={`/mis-empresas/${c.id}/gri-306`} className="text-sm font-semibold px-4 py-2 rounded-full bg-[#00f2a1] text-[#05130e] hover:bg-[#33f5b4] transition-colors" data-testid={`companies-gri-open-${c.id}`}>Abrir informe · {c.resumen?.razon_social}</Link>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-300" data-testid="companies-gri-hint">{gens.length ? "Tu Empresa Generadora aún no está validada. Sube sus 4 documentos y valídala para abrir el informe." : "Agrega y valida una Empresa Generadora para generar el informe con tus lotes y retiros."}</p>
      )}
    </section>
  );
};

export default function Companies() {
  const api = useApi();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [reading, setReading] = useState(false);

  useEffect(() => {
    api("get", "/companies").then((r) => setList(r.data)).catch(() => setList([]));
  }, [api]);

  const add = async (role) => {
    setReading(true);
    try {
      const { data } = await api("post", "/companies", { role });
      navigate(`/mis-empresas/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setReading(false);
    }
  };

  return (
    <main className="max-w-4xl mx-auto px-5 lg:px-8 py-12 space-y-8" data-testid="companies-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00b37a] mb-2">Mis empresas</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#05130e]">Empresas asociadas a tu cuenta</h1>
        <p className="text-slate-600 mt-2">Una empresa puede participar como Generadora, como Gestora / Destinataria, como Empresa de Transporte o en varios roles. Cada rol pide sus propios documentos oficiales; leemos y guardamos todos sus datos y tú los confirmas.</p>
      </div>
      <section className="grid sm:grid-cols-3 gap-4" data-testid="company-add-section">
        {[["generador", Factory, "E-RUT, Registro RETC, Certificado SINADER / SIDREP y Reporte SNIFA"], ["gestor", Recycle, "Resolución Sanitaria con QR y CVE, Registro RETC y E-RUT"], ["transportista", Truck, "Resolución Sanitaria de Transporte con nómina de patentes e Inscripción en el Registro de Transportistas del RETC"]].map(([role, Icon, docs]) => (
          <button key={role} disabled={reading} onClick={() => add(role)} data-testid={`company-add-${role}`}
            className="text-left bg-white border-2 border-dashed border-slate-300 hover:border-[#00c487] rounded-3xl p-6 transition-colors disabled:opacity-60">
            {reading ? <Loader2 className="w-7 h-7 animate-spin text-[#00b37a]" /> : <Icon className="w-7 h-7 text-[#00b37a]" />}
            <p className="font-semibold text-[#05130e] mt-3">+ Agregar {ROLE_LABEL[role]}</p>
            <p className="text-xs text-slate-500 mt-1">{docs}</p>
          </button>
        ))}
      </section>
      <GriSection list={list} />
      <section className="space-y-3">
        {list?.length === 0 && <p className="text-sm text-slate-500" data-testid="companies-empty">Aún no tienes empresas asociadas.</p>}
        {list?.map((c) => (
          <div key={c.id} className="relative flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#00c487] transition-colors">
            <Link to={`/mis-empresas/${c.id}`} className="absolute inset-0 rounded-2xl" aria-label={c.resumen?.razon_social || "Empresa"} data-testid={`company-row-${c.id}`} />
            <span className="flex items-center gap-3 pointer-events-none">
              <Building2 className="w-5 h-5 text-[#00b37a]" />
              <span>
                <span className="block font-semibold text-[#05130e]">{c.resumen?.razon_social || "Empresa sin razón social"} <span className="ml-1 text-[11px] font-mono uppercase text-[#00915f]">{ROLE_LABEL[c.role]}</span></span>
                <span className="block text-xs font-mono text-slate-500">RUT {c.resumen?.rut || "—"} · {c.role === "transportista" ? `Patentes ${c.resumen?.patentes_autorizadas || "—"}` : `Establecimiento ${c.resumen?.id_establecimiento_vu || "—"}`}</span>
              </span>
            </span>
            <span className="flex items-center gap-3">
              {c.role === "generador" && (c.status === "validada" ? (
                <Link to={`/mis-empresas/${c.id}/gri-306`} className="relative z-10 text-xs font-semibold px-3 py-1 rounded-full bg-[#0a1f18] text-[#00f2a1] hover:bg-[#0f2c22]" data-testid={`company-gri-btn-${c.id}`}>Informe GRI 306</Link>
              ) : (
                <span className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-500 pointer-events-none" data-testid={`company-gri-locked-${c.id}`}>Informe GRI 306 · al validar</span>
              ))}
              <span className="pointer-events-none"><CompanyStatus status={c.status} /></span>
            </span>
          </div>
        ))}
      </section>
    </main>
  );
}
